import { useSyncExternalStore } from 'react';

// =====================================================
// DEBUG LOG STORE - ONEFORM
// Singleton in-memory para diagnóstico do fluxo de auth
// =====================================================

export type LogLevel = 'info' | 'warn' | 'error' | 'success';

export interface DebugLogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  category: string;
  message: string;
  data?: unknown;
}

type Subscriber = () => void;

const MAX_LOGS = 500;

let logs: DebugLogEntry[] = [];
const subscribers = new Set<Subscriber>();
let idCounter = 0;

function notify() {
  subscribers.forEach((fn) => fn());
}

/**
 * Registra um log de diagnóstico.
 * Em dev: sempre ativo. Em prod: só se localStorage 'oneform_debug' === 'true'.
 */
export function debugLog(
  level: LogLevel,
  category: string,
  message: string,
  data?: unknown,
): void {
  if (import.meta.env.PROD && localStorage.getItem('oneform_debug') !== 'true') {
    return;
  }

  const entry: DebugLogEntry = {
    id: String(++idCounter),
    timestamp: new Date(),
    level,
    category,
    message,
    data,
  };

  logs = [...logs.slice(-(MAX_LOGS - 1)), entry];
  notify();

  // Espelhar no console do browser
  const consoleFn =
    level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
  consoleFn(`[${category}] ${message}`, data ?? '');
}

export function getLogs(): DebugLogEntry[] {
  return logs;
}

export function clearLogs(): void {
  logs = [];
  idCounter = 0;
  notify();
}

export function subscribe(fn: Subscriber): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

/**
 * React hook — re-renderiza quando novos logs chegam.
 * Usa useSyncExternalStore para compatibilidade com React 19 concurrent mode.
 */
export function useDebugLogs(): DebugLogEntry[] {
  return useSyncExternalStore(subscribe, getLogs, getLogs);
}
